import { useEffect } from "react";

import { GLOBAL_KEYS, type GlobalKeyHandlers } from "./keymap";

export type { GlobalKeyHandlers };

// Global keyboard shortcuts, all driven by the shared GLOBAL_KEYS table
// (command-palette-and-search.md §Keyboard): meta shortcuts (⌘K) fire even
// while typing; single-key shortcuts (?, N) and the `G`-prefixed nav chords
// (G then H → repositories, G then S → settings, within an 800ms window) fire
// only when not typing in an input/textarea.
export const useGlobalKeys = (handlers: GlobalKeyHandlers): void => {
  useEffect(() => {
    let gPending = false;
    let gTimer: ReturnType<typeof setTimeout> | undefined;
    const resetG = (): void => {
      gPending = false;
      if (gTimer !== undefined) {
        clearTimeout(gTimer);
        gTimer = undefined;
      }
    };
    const hasChords = GLOBAL_KEYS.some((b) => b.chord !== undefined);

    const onKey = (e: KeyboardEvent): void => {
      const el = document.activeElement;
      const typing =
        el instanceof HTMLElement &&
        (el.tagName === "INPUT" ||
          el.tagName === "TEXTAREA" ||
          el.isContentEditable);
      const k = e.key.toLowerCase();
      const meta = e.metaKey || e.ctrlKey;

      // Meta shortcuts (⌘K) fire even from within an input.
      if (meta) {
        const metaBinding = GLOBAL_KEYS.find((b) => b.meta && b.key === k);
        if (metaBinding) {
          e.preventDefault();
          handlers[metaBinding.action]();
        }
        return;
      }
      if (typing || e.altKey) {
        return;
      }

      // Complete a pending `G` chord (any non-chord key cancels it).
      if (gPending) {
        const chord = GLOBAL_KEYS.find((b) => b.chord === k);
        resetG();
        if (chord) {
          e.preventDefault();
          handlers[chord.action]();
        }
        return;
      }
      // Begin a `G` chord.
      if (k === "g" && hasChords) {
        gPending = true;
        gTimer = setTimeout(() => {
          gPending = false;
        }, 800);
        return;
      }
      // Single-key (non-meta) shortcuts. Match the raw key too so "?" resolves.
      const binding = GLOBAL_KEYS.find(
        (b) =>
          !b.meta && b.chord === undefined && (b.key === k || b.key === e.key),
      );
      if (binding) {
        e.preventDefault();
        handlers[binding.action]();
      }
    };

    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      resetG();
    };
  }, [handlers]);
};
